import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SellerLayout from './SellerLayout';

/**
 * SellerSmartfarmRequestList.jsx
 *
 * 예상 API
 *
 * GET  /api/seller/smartfarm/request-list
 *   params:
 *   - page
 *   - per_page
 *   - keyword
 *   - status
 *
 * POST /api/seller/smartfarm/request-status
 *   body:
 *   - request_id
 *   - status
 *   - admin_memo
 *
 * POST /api/seller/smartfarm/request-memo
 *   body:
 *   - request_id
 *   - admin_memo
 *
 * 응답 예시
 * {
 *   ok: true,
 *   requests: [...],
 *   pagination: {
 *     page: 1,
 *     per_page: 20,
 *     total: 35,
 *     total_pages: 2
 *   },
 *   counts: {
 *     ALL: 35,
 *     REQUEST: 10,
 *     SETTING: 8,
 *     COMPLETE: 12,
 *     REJECT: 3,
 *     CANCEL: 2
 *   }
 * }
 */

const SellerSmartfarmRequestList = () => {
  const navigate = useNavigate();

  const api = useMemo(
    () =>
      axios.create({
        withCredentials: true,
      }),
    []
  );

  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({
    ALL: 0,
    REQUEST: 0,
    SETTING: 0,
    COMPLETE: 0,
    REJECT: 0,
    CANCEL: 0,
  });

  const [filters, setFilters] = useState({
    keyword: '',
    status: 'ALL',
  });

  const [appliedFilters, setAppliedFilters] = useState({
    keyword: '',
    status: 'ALL',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  });

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailForm, setDetailForm] = useState({
    status: '',
    admin_memo: '',
  });

  const [loading, setLoading] = useState(true);
  const [detailSaving, setDetailSaving] = useState(false);
  const [memoSaving, setMemoSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const statusTabs = [
    { value: 'ALL', label: '전체' },
    { value: 'REQUEST', label: '접수' },
    { value: 'SETTING', label: '설치중' },
    { value: 'COMPLETE', label: '완료' },
    { value: 'REJECT', label: '반려' },
    { value: 'CANCEL', label: '취소' },
  ];

  const statusOptions = [
    { value: 'REQUEST', label: '신청 접수' },
    { value: 'SETTING', label: '설치 준비 중' },
    { value: 'COMPLETE', label: '설치 완료' },
    { value: 'REJECT', label: '신청 반려' },
    { value: 'CANCEL', label: '신청 취소' },
  ];

  const handleApiError = useCallback(
    (err, fallbackMessage) => {
      const status = err?.response?.status;
      const redirect = err?.response?.data?.redirect;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        fallbackMessage;

      if (status === 401 || status === 403) {
        navigate(redirect || '/seller/login', {
          replace: true,
        });
        return;
      }

      setError(message);
    },
    [navigate]
  );

  const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  };

  const getStatusText = (status) => {
    const labels = {
      REQUEST: '신청 접수',
      SETTING: '설치 준비 중',
      COMPLETE: '설치 완료',
      REJECT: '신청 반려',
      CANCEL: '신청 취소',
    };

    return labels[status] || status || '-';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'COMPLETE') return 'text-bg-success';
    if (status === 'SETTING') return 'text-bg-warning';
    if (status === 'REQUEST') return 'text-bg-primary';
    if (status === 'REJECT') return 'text-bg-danger';
    return 'text-bg-secondary';
  };

  const normalizeRequest = (item) => ({
    ...item,
    member_name:
      item.member_name ||
      item.member_nickname ||
      item.nickname ||
      item.user_name ||
      '-',
    member_email:
      item.member_email ||
      item.email ||
      '',
    member_phone:
      item.member_phone ||
      item.phone ||
      '',
    status: item.status || 'REQUEST',
    memo: item.memo || '',
    admin_memo: item.admin_memo || '',
  });

  const loadRequests = useCallback(
    async (page = 1, filtersToUse = appliedFilters) => {
      setLoading(true);
      setError('');

      try {
        const response = await api.get(
          '/api/seller/smartfarm/request-list',
          {
            params: {
              page,
              per_page: pagination.per_page,
              keyword: filtersToUse.keyword || undefined,
              status:
                filtersToUse.status === 'ALL'
                  ? undefined
                  : filtersToUse.status,
            },
          }
        );

        const data = response.data || {};

        if (!data.ok) {
          throw new Error(
            data.error ||
              '스마트팜 신청 목록을 불러오지 못했습니다.'
          );
        }

        const nextRequests = Array.isArray(data.requests)
          ? data.requests.map(normalizeRequest)
          : [];

        setRequests(nextRequests);

        setCounts({
          ALL: data.counts?.ALL ?? data.pagination?.total ?? 0,
          REQUEST: data.counts?.REQUEST ?? 0,
          SETTING: data.counts?.SETTING ?? 0,
          COMPLETE: data.counts?.COMPLETE ?? 0,
          REJECT: data.counts?.REJECT ?? 0,
          CANCEL: data.counts?.CANCEL ?? 0,
        });

        setPagination((prev) => ({
          ...prev,
          page: data.pagination?.page ?? page,
          per_page:
            data.pagination?.per_page ?? prev.per_page,
          total: data.pagination?.total ?? nextRequests.length,
          total_pages:
            data.pagination?.total_pages ?? 1,
        }));

        setSelectedRequest((prev) => {
          if (!prev) return null;

          const updated = nextRequests.find(
            (item) => item.id === prev.id
          );

          return updated || prev;
        });
      } catch (err) {
        handleApiError(
          err,
          '스마트팜 신청 목록을 불러오지 못했습니다.'
        );
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      appliedFilters,
      handleApiError,
      pagination.per_page,
    ]
  );

  useEffect(() => {
    loadRequests(1, appliedFilters);
  }, [appliedFilters, loadRequests]);

  const applySearch = (event) => {
    event.preventDefault();

    setAppliedFilters({
      keyword: filters.keyword.trim(),
      status: filters.status,
    });
  };

  const changeStatusTab = (status) => {
    const next = {
      keyword: filters.keyword.trim(),
      status,
    };

    setFilters((prev) => ({
      ...prev,
      status,
    }));

    setAppliedFilters(next);
  };

  const resetFilters = () => {
    const reset = {
      keyword: '',
      status: 'ALL',
    };

    setFilters(reset);
    setAppliedFilters(reset);
  };

  const openDetail = (request) => {
    setSelectedRequest(request);
    setDetailForm({
      status: request.status || 'REQUEST',
      admin_memo: request.admin_memo || '',
    });
    setError('');
    setNotice('');
  };

  const closeDetail = () => {
    if (detailSaving || memoSaving) return;
    setSelectedRequest(null);
  };

  const updateLocalRequest = (requestId, patch) => {
    setRequests((prev) =>
      prev.map((item) =>
        item.id === requestId
          ? { ...item, ...patch }
          : item
      )
    );

    setSelectedRequest((prev) =>
      prev?.id === requestId
        ? { ...prev, ...patch }
        : prev
    );
  };

  const saveMemo = async () => {
    if (!selectedRequest || memoSaving) return;

    setMemoSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/seller/smartfarm/request-memo',
        {
          request_id: selectedRequest.id,
          admin_memo: detailForm.admin_memo.trim(),
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '관리자 메모 저장에 실패했습니다.'
        );
      }

      updateLocalRequest(selectedRequest.id, {
        admin_memo: detailForm.admin_memo.trim(),
        updated_at:
          data.request?.updated_at ||
          new Date().toISOString(),
      });

      setNotice(
        data.message || '관리자 메모를 저장했습니다.'
      );
    } catch (err) {
      handleApiError(
        err,
        '관리자 메모 저장에 실패했습니다.'
      );
    } finally {
      setMemoSaving(false);
    }
  };

  const saveStatus = async () => {
    if (!selectedRequest || detailSaving) return;

    setDetailSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/seller/smartfarm/request-status',
        {
          request_id: selectedRequest.id,
          status: detailForm.status,
          admin_memo: detailForm.admin_memo.trim(),
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '신청 상태 변경에 실패했습니다.'
        );
      }

      updateLocalRequest(selectedRequest.id, {
        status: detailForm.status,
        admin_memo: detailForm.admin_memo.trim(),
        updated_at:
          data.request?.updated_at ||
          new Date().toISOString(),
      });

      setNotice(
        data.message || '신청 상태를 변경했습니다.'
      );

      await loadRequests(
        pagination.page,
        appliedFilters
      );
    } catch (err) {
      handleApiError(
        err,
        '신청 상태 변경에 실패했습니다.'
      );
    } finally {
      setDetailSaving(false);
    }
  };

  const goToCreateFarm = () => {
    if (!selectedRequest) return;

    const params = new URLSearchParams();

    if (selectedRequest.member_id) {
      params.set(
        'member_id',
        String(selectedRequest.member_id)
      );
    }

    if (selectedRequest.id) {
      params.set(
        'request_id',
        String(selectedRequest.id)
      );
    }

    navigate(
      `/seller/smartfarm/add?${params.toString()}`
    );
  };

  const getPageNumbers = () => {
    const total = pagination.total_pages;
    const current = pagination.page;
    const numbers = [];

    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    for (let page = start; page <= end; page += 1) {
      numbers.push(page);
    }

    return numbers;
  };

  const renderDesktopTable = () => (
    <div className="table-responsive d-none d-lg-block">
      <table className="table align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: 80 }}>번호</th>
            <th>회원</th>
            <th>신청 내용</th>
            <th style={{ width: 140 }}>상태</th>
            <th style={{ width: 170 }}>신청일</th>
            <th
              className="text-end"
              style={{ width: 110 }}
            >
              관리
            </th>
          </tr>
        </thead>

        <tbody>
          {requests.map((request) => (
            <tr key={request.id}>
              <td className="text-secondary">
                {request.id}
              </td>

              <td>
                <div className="fw-semibold">
                  {request.member_name}
                </div>

                {request.member_email && (
                  <div className="small text-secondary">
                    {request.member_email}
                  </div>
                )}

                {request.member_phone && (
                  <div className="small text-secondary">
                    {request.member_phone}
                  </div>
                )}
              </td>

              <td>
                <div
                  className="text-truncate"
                  style={{ maxWidth: 420 }}
                >
                  {request.memo || '신청 내용 없음'}
                </div>

                {request.admin_memo && (
                  <div
                    className="small text-primary text-truncate mt-1"
                    style={{ maxWidth: 420 }}
                  >
                    관리자 메모: {request.admin_memo}
                  </div>
                )}
              </td>

              <td>
                <span
                  className={`badge rounded-pill ${getStatusBadgeClass(
                    request.status
                  )}`}
                >
                  {getStatusText(request.status)}
                </span>
              </td>

              <td className="small text-secondary">
                {formatDateTime(request.created_at)}
              </td>

              <td className="text-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openDetail(request)}
                >
                  상세
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderMobileCards = () => (
    <div className="d-lg-none">
      {requests.map((request) => (
        <div
          key={request.id}
          className="border-bottom p-3"
        >
          <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
            <div>
              <div className="fw-bold">
                {request.member_name}
              </div>

              <div className="small text-secondary">
                신청번호 #{request.id}
              </div>
            </div>

            <span
              className={`badge rounded-pill ${getStatusBadgeClass(
                request.status
              )}`}
            >
              {getStatusText(request.status)}
            </span>
          </div>

          <div
            className="small mb-3"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'pre-line',
            }}
          >
            {request.memo || '신청 내용 없음'}
          </div>

          <div className="d-flex justify-content-between align-items-center gap-2">
            <span className="small text-secondary">
              {formatDateTime(request.created_at)}
            </span>

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => openDetail(request)}
            >
              상세 보기
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <SellerLayout>
      <div className="container-fluid px-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">
            스마트팜 신청 관리
          </h2>
          <p className="text-secondary mb-0">
            회원 신청 내용을 확인하고 설치 진행 상태를
            관리합니다.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() =>
            navigate('/seller/smartfarm')
          }
        >
          스마트팜 목록
        </button>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setError('')}
          />
        </div>
      )}

      {notice && (
        <div
          className="alert alert-success d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{notice}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setNotice('')}
          />
        </div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-3">
          <div className="d-flex flex-nowrap overflow-auto gap-2 pb-1">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`btn text-nowrap ${
                  appliedFilters.status === tab.value
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                }`}
                onClick={() =>
                  changeStatusTab(tab.value)
                }
              >
                {tab.label}
                <span className="ms-2">
                  {counts[tab.value] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <form
        className="card border-0 shadow-sm mb-4"
        onSubmit={applySearch}
      >
        <div className="card-body">
          <div className="row g-2">
            <div className="col-12 col-md">
              <input
                type="search"
                className="form-control"
                value={filters.keyword}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    keyword: event.target.value,
                  }))
                }
                placeholder="회원명, 이메일, 전화번호, 신청 내용 검색"
              />
            </div>

            <div className="col-6 col-md-auto">
              <button
                type="submit"
                className="btn btn-primary w-100"
              >
                검색
              </button>
            </div>

            <div className="col-6 col-md-auto">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={resetFilters}
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="card border-0 shadow-sm overflow-hidden">
        <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <div className="fw-bold">
            신청 목록
          </div>

          <div className="small text-secondary">
            총 {pagination.total}건
          </div>
        </div>

        {loading ? (
          <div className="py-5 text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              aria-label="로딩 중"
            />

            <div className="mt-3 text-secondary">
              신청 목록을 불러오고 있습니다.
            </div>
          </div>
        ) : requests.length === 0 ? (
          <div className="py-5 text-center">
            <div className="fs-2 mb-3">📭</div>
            <div className="fw-bold mb-1">
              신청 내역이 없습니다
            </div>
            <div className="text-secondary">
              검색 조건을 변경해 다시 확인하세요.
            </div>
          </div>
        ) : (
          <>
            {renderDesktopTable()}
            {renderMobileCards()}
          </>
        )}

        {!loading &&
          pagination.total_pages > 1 && (
            <div className="card-footer bg-white py-3">
              <nav aria-label="페이지 이동">
                <ul className="pagination justify-content-center mb-0 flex-wrap">
                  <li
                    className={`page-item ${
                      pagination.page <= 1
                        ? 'disabled'
                        : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="page-link"
                      onClick={() =>
                        loadRequests(
                          pagination.page - 1,
                          appliedFilters
                        )
                      }
                    >
                      이전
                    </button>
                  </li>

                  {getPageNumbers().map((page) => (
                    <li
                      key={page}
                      className={`page-item ${
                        pagination.page === page
                          ? 'active'
                          : ''
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() =>
                          loadRequests(
                            page,
                            appliedFilters
                          )
                        }
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li
                    className={`page-item ${
                      pagination.page >=
                      pagination.total_pages
                        ? 'disabled'
                        : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="page-link"
                      onClick={() =>
                        loadRequests(
                          pagination.page + 1,
                          appliedFilters
                        )
                      }
                    >
                      다음
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
      </div>

      {selectedRequest && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold">
                      스마트팜 신청 상세
                    </h5>
                    <div className="small text-secondary">
                      신청번호 #{selectedRequest.id}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    aria-label="닫기"
                    onClick={closeDetail}
                    disabled={
                      detailSaving || memoSaving
                    }
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-secondary mb-1">
                          회원
                        </div>
                        <div className="fw-bold">
                          {selectedRequest.member_name}
                        </div>

                        {selectedRequest.member_email && (
                          <div className="small mt-1">
                            {selectedRequest.member_email}
                          </div>
                        )}

                        {selectedRequest.member_phone && (
                          <div className="small mt-1">
                            {selectedRequest.member_phone}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="border rounded p-3 h-100">
                        <div className="small text-secondary mb-1">
                          신청 정보
                        </div>

                        <div>
                          신청일:{' '}
                          {formatDateTime(
                            selectedRequest.created_at
                          )}
                        </div>

                        <div className="mt-1">
                          최근 수정:{' '}
                          {formatDateTime(
                            selectedRequest.updated_at ||
                              selectedRequest.created_at
                          )}
                        </div>

                        <div className="mt-2">
                          <span
                            className={`badge rounded-pill ${getStatusBadgeClass(
                              selectedRequest.status
                            )}`}
                          >
                            {getStatusText(
                              selectedRequest.status
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      회원 신청 내용
                    </label>

                    <div
                      className="bg-light rounded p-3"
                      style={{
                        whiteSpace: 'pre-wrap',
                        minHeight: 120,
                      }}
                    >
                      {selectedRequest.memo ||
                        '신청 내용이 없습니다.'}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="requestStatus"
                      className="form-label fw-bold"
                    >
                      처리 상태
                    </label>

                    <select
                      id="requestStatus"
                      className="form-select"
                      value={detailForm.status}
                      onChange={(event) =>
                        setDetailForm((prev) => ({
                          ...prev,
                          status: event.target.value,
                        }))
                      }
                    >
                      {statusOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="adminMemo"
                      className="form-label fw-bold"
                    >
                      관리자 메모
                    </label>

                    <textarea
                      id="adminMemo"
                      className="form-control"
                      rows={6}
                      maxLength={2000}
                      value={detailForm.admin_memo}
                      onChange={(event) =>
                        setDetailForm((prev) => ({
                          ...prev,
                          admin_memo:
                            event.target.value,
                        }))
                      }
                      placeholder="회원에게 전달할 안내나 설치 진행 내용을 입력하세요."
                    />

                    <div className="form-text text-end">
                      {detailForm.admin_memo.length}/2000
                    </div>
                  </div>
                </div>

                <div className="modal-footer d-flex flex-column flex-sm-row justify-content-between gap-2">
                  <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-sm-auto">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={goToCreateFarm}
                    >
                      이 회원으로 농장 등록
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={saveMemo}
                      disabled={
                        memoSaving || detailSaving
                      }
                    >
                      {memoSaving
                        ? '메모 저장 중...'
                        : '메모만 저장'}
                    </button>
                  </div>

                  <div className="d-flex gap-2 w-100 w-sm-auto">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={closeDetail}
                      disabled={
                        detailSaving || memoSaving
                      }
                    >
                      닫기
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary flex-fill"
                      onClick={saveStatus}
                      disabled={
                        detailSaving || memoSaving
                      }
                    >
                      {detailSaving
                        ? '저장 중...'
                        : '상태 저장'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}
      </div>
    </SellerLayout>
  );
};

export default SellerSmartfarmRequestList;